import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../../shared/material.module';
import { ToastService } from '../../../services/toast.service';
import { TenantService } from '../../../services/tenant.service';
import { Tenant } from '../../../models/tenant.model';

export interface TenantSetPasswordData {
  tenant: Tenant;
}

@Component({
  selector: 'app-tenant-set-password',
  standalone: true,
  imports: [ReactiveFormsModule, MaterialModule],
  template: `
    <div class="dialog-container">
      <div class="dialog-header">
        <h2 mat-dialog-title>Set Password for {{ data.tenant.name }}</h2>
        <button mat-icon-button (click)="onCancel()" class="close-button">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      
      <mat-dialog-content>
        <div class="password-form">
          <div class="tenant-info">
            <mat-icon>business</mat-icon>
            <div class="tenant-details">
              <h3>{{ data.tenant.name }}</h3>
              <p>{{ data.tenant.slug }}</p>
            </div>
          </div>
          
          <form [formGroup]="passwordForm" class="form-content">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>New Password</mat-label>
              <input matInput 
                     type="password" 
                     formControlName="password" 
                     placeholder="Enter new password">
              @if (passwordForm.get('password')?.hasError('required')) {
                <mat-error>Password is required</mat-error>
              }
              @if (passwordForm.get('password')?.hasError('minlength')) {
                <mat-error>Password must be at least 6 characters</mat-error>
              }
            </mat-form-field>
            
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Confirm Password</mat-label>
              <input matInput 
                     type="password" 
                     formControlName="confirmPassword" 
                     placeholder="Confirm new password">
              @if (passwordForm.get('confirmPassword')?.hasError('required')) {
                <mat-error>Please confirm the password</mat-error>
              }
              @if (passwordForm.get('confirmPassword')?.hasError('passwordMismatch')) {
                <mat-error>Passwords do not match</mat-error>
              }
            </mat-form-field>
            
            <div class="password-info">
              <mat-icon>info</mat-icon>
              <p>This will set the password for all users belonging to this tenant.</p>
            </div>
          </form>
        </div>
      </mat-dialog-content>

      <mat-dialog-actions align="end">
        <button mat-button (click)="onCancel()">
          <mat-icon>close</mat-icon>
          Cancel
        </button>
        <button mat-raised-button 
                color="primary" 
                (click)="onSetPassword()"
                [disabled]="passwordForm.invalid || isLoading">
          @if (isLoading) {
            <mat-spinner diameter="20"></mat-spinner>
          }
          @if (!isLoading) {
            <mat-icon>lock_reset</mat-icon>
          }
          @if (!isLoading) {
            <span>Set Password</span>
          }
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .dialog-container {
      padding: 0;
    }
    
    .dialog-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1.5rem 1.5rem 0 1.5rem;
      border-bottom: 1px solid #e5e7eb;
    }
    
    .dialog-header h2 {
      margin: 0;
      color: #0c2864;
    }
    
    .close-button {
      margin-left: auto;
    }
    
    .password-form {
      padding: 1.5rem;
    }
    
    .tenant-info {
      display: flex;
      align-items: center;
      gap: 1rem;
      margin-bottom: 2rem;
      padding: 1rem;
      background: #f8fafc;
      border-radius: 8px;
      border: 1px solid #e5e7eb;
    }
    
    .tenant-info mat-icon {
      font-size: 2rem;
      color: #0c2864;
    }
    
    .tenant-details h3 {
      margin: 0 0 0.25rem 0;
      color: #0c2864;
    }
    
    .tenant-details p {
      margin: 0;
      color: #6b7280;
      font-size: 0.9rem;
    }
    
    .form-content {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }
    
    .full-width {
      width: 100%;
    }
    
    .password-info {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-top: 1rem;
      padding: 1rem;
      background: #dbeafe;
      border-radius: 6px;
      border-left: 4px solid #3b82f6;
    }
    
    .password-info mat-icon {
      color: #3b82f6;
    }
    
    .password-info p {
      margin: 0;
      color: #1e40af;
      font-size: 0.9rem;
    }
    
    mat-dialog-actions {
      padding: 1rem 1.5rem;
      border-top: 1px solid #e5e7eb;
    }
    
    mat-dialog-actions button {
      margin-left: 0.5rem;
    }
  `]
})
export class TenantSetPasswordComponent {
  passwordForm: FormGroup;
  isLoading = false;

  constructor(
    private fb: FormBuilder,
    private tenantService: TenantService,
    private toastService: ToastService,
    public dialogRef: MatDialogRef<TenantSetPasswordComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TenantSetPasswordData
  ) {
    this.passwordForm = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });
  }

  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password');
    const confirmPassword = form.get('confirmPassword');
    
    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }
    
    return null;
  }

  onSetPassword(): void {
    if (this.passwordForm.valid) {
      this.isLoading = true;
      
      this.tenantService.setTenantPassword(this.data.tenant.id, this.passwordForm.value.password)
        .subscribe({
          next: (response: any) => {
            this.isLoading = false;
            this.toastService.success('Password Set', 'Password has been set successfully for all tenant users');
            this.dialogRef.close(true);
          },
          error: (error: any) => {
            this.isLoading = false;
            this.toastService.error('Error', error.error?.message || 'Failed to set password');
            console.error('Error setting password:', error);
          }
        });
    }
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
